import React, { useEffect, useState } from "react";
import { ChartContainer } from "../view/ChartContainer";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { CustomTooltip } from "./components/CustomTooltip";
import { transformLineChartData } from "./utils/chartDataTransformers";
import { COMPLIANCE_COLORS, DEVICE_COLORS_10 } from "./constants/chartColors";
import * as statisticService from "./statisticService";
import { AuditScope } from "./chartDynamicLabels";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { InteractiveLegend } from "./components/InteractiveLegend";
import DashboardChartSkeleton from "./components/DashboardChartSkeleton";

function ComplianceChart({ fromDate, toDate, scope = AuditScope.BY_DAY }) {



  const [rechartsData, setRechartsData] = useState([]);
  const [lineConfigs, setLineConfigs] = useState([]);
  const [overallData, setOverallData] = useState(null);
  const [viewMode, setViewMode] = useState('OVERALL'); // 'OVERALL' or 'BREAKDOWN'
  const [hiddenSeries, setHiddenSeries] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const cpData = await statisticService.getCompliance(scope);
      if (cpData && cpData.labels && cpData.datasets) {

        setOverallData({ ...cpData });
        setViewMode('OVERALL');

        // Transform for Recharts
        const transformed = transformLineChartData(cpData);
        setRechartsData(transformed);

        // Create line configs
        const configs = cpData.datasets.map(dataset => ({
          dataKey: dataset.label.toLowerCase().replace(/\s+/g, '_'),
          name: dataset.label === 'Compliance' ? 'Compliance %' : dataset.label,
          stroke: dataset.borderColor,
          strokeWidth: 3
        }));
        setLineConfigs(configs);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (scope !== AuditScope.CUSTOM)
      fetchData();
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [scope])

  const fetchDataCustom = async () => {
    setIsLoading(true);
    try {
      const cpData = await statisticService.getComplianceCustom(fromDate, toDate);
      if (cpData && cpData.labels && cpData.datasets) {

        setOverallData({ ...cpData });
        setViewMode('OVERALL');

        // Transform for Recharts
        const transformed = transformLineChartData(cpData);
        setRechartsData(transformed);

        // Create line configs with modern colors
        const configs = cpData.datasets.map(dataset => {
          const isCompliance = dataset.label === 'Compliance';
          const isTarget = dataset.label === 'Target';
          return {
            dataKey: dataset.label.toLowerCase().replace(/\s+/g, '_'),
            name: isCompliance ? 'Compliance %' : dataset.label,
            stroke: isCompliance ? COMPLIANCE_COLORS.compliance :
              isTarget ? COMPLIANCE_COLORS.target :
                dataset.borderColor,
            strokeWidth: 3
          };
        });
        setLineConfigs(configs);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (scope !== AuditScope.CUSTOM) {
      return;
    }

    const fromNotToday = isDateDifferentThanToday(fromDate);
    const toNotToday = isDateDifferentThanToday(toDate);
    checkCustomDatesSelected(fromNotToday, toNotToday);
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [fromDate, toDate, scope])

  const checkCustomDatesSelected = (isFromDate, isToDate) => {
    if (isFromDate === true || isToDate === true) {
      fetchDataCustom();
    }
  }

  const isDateDifferentThanToday = (dateToCheck) => {
    if (!dateToCheck) {
      return false;
    }
    const today = new Date();
    const isSameDay =
      dateToCheck.getFullYear() === today.getFullYear() &&
      dateToCheck.getMonth() === today.getMonth() &&
      dateToCheck.getDate() === today.getDate();

    return !isSameDay;
  };

  // Handle legend click to toggle series visibility
  const handleToggleSeries = (dataKey) => {
    setHiddenSeries(prev => ({
      ...prev,
      [dataKey]: !prev[dataKey]
    }));
  };

  // Handle line click to show device-level compliance breakdown
  const handleLineClick = async (data, dataKey) => {
    // Prevent drill-down if already in breakdown mode
    if (viewMode === 'BREAKDOWN') return;

    // Only allow clicking on the "Compliance" line
    if (dataKey !== 'compliance') return;

    // Determine effective scope
    let effectiveScope = scope;
    if (scope === AuditScope.CUSTOM && (isDateDifferentThanToday(fromDate) || isDateDifferentThanToday(toDate))) {
      effectiveScope = AuditScope.CUSTOM;
    }

    try {
      const deviceTrends = await statisticService.getDeviceTrends(effectiveScope, fromDate, toDate);

      if (deviceTrends && deviceTrends.length > 0) {
        // Calculate average compliance for each device to sort them
        const sortedTrends = deviceTrends.map(device => {
          const sum = device.data.reduce((a, b) => a + b, 0);
          const avg = sum / device.data.length;
          return { ...device, averageCompliance: avg };
        }).sort((a, b) => b.averageCompliance - a.averageCompliance);

        // Take top 10
        const top10Trends = sortedTrends.slice(0, 10);

        // Build Recharts data format with all devices
        const labels = overallData.labels;
        const transformedData = labels.map((label, idx) => {
          const point = { label };
          top10Trends.forEach((device) => {
            const deviceKey = device.deviceName.toLowerCase().replace(/\s+/g, '_');
            point[deviceKey] = device.data[idx];
          });
          return point;
        });

        // Create line configs for devices
        const deviceConfigs = top10Trends.map((device, index) => ({
          dataKey: device.deviceName.toLowerCase().replace(/\s+/g, '_'),
          name: device.deviceName,
          stroke: DEVICE_COLORS_10[index % DEVICE_COLORS_10.length],
          strokeWidth: 3
        }));

        setRechartsData(transformedData);
        setLineConfigs(deviceConfigs);
        setViewMode('BREAKDOWN');
      }

    } catch (error) {
      console.error('Error fetching device trends:', error);
    }
  };

  const handleBackClick = () => {
    if (overallData) {
      // Transform back to overall view
      const transformed = transformLineChartData(overallData);
      setRechartsData(transformed);

      // Restore overall configs with modern colors
      const configs = overallData.datasets.map(dataset => {
        const isCompliance = dataset.label === 'Compliance';
        const isTarget = dataset.label === 'Target';
        return {
          dataKey: dataset.label.toLowerCase().replace(/\s+/g, '_'),
          name: isCompliance ? 'Compliance %' : dataset.label,
          stroke: isCompliance ? COMPLIANCE_COLORS.compliance :
            isTarget ? COMPLIANCE_COLORS.target :
              dataset.borderColor,
          strokeWidth: 3
        };
      });
      setLineConfigs(configs);
      setViewMode('OVERALL');
    }
  };

  if (isLoading) {
    return <DashboardChartSkeleton title="Compliance %" subtitle="Overall" />;
  }

  return (
    <ChartContainer noHeader={true} className="oct_chart-card-v2">
      <div className="oct_chart-header-with-action-v2">
        <div className="oct_chart-info-v2">
          <p className="oct_chart-subtitle-v2">Overall</p>
          <h3 className="oct_chart-title-v2">Compliance %</h3>
        </div>
        {viewMode === 'BREAKDOWN' && (
          <button
            onClick={handleBackClick}
            className="oct_chart-back-btn-v2"
          >
            <ArrowBackIcon className="back-arrow-v2" />
            <span>Go Back</span>
          </button>
        )}
      </div>
      <div className="oct_interactive-legend-wrapper-v2">
        {lineConfigs.length > 0 && (
          <InteractiveLegend
            payload={lineConfigs.map(config => ({
              value: config.name,
              color: config.stroke,
              dataKey: config.dataKey
            }))}
            hiddenSeries={hiddenSeries}
            onToggleSeries={handleToggleSeries}
          />
        )}
      </div>
      <div className="oct_recharts-wrapper-v2">
        <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 10, height: 10 }}>
          <LineChart data={rechartsData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              dataKey="label"
              stroke="#94a3b8"
              tick={{ fontSize: 12 }}
            />
            <YAxis
              stroke="#94a3b8"
              tick={{ fontSize: 12 }}
            />
            <Tooltip content={<CustomTooltip />} />
            {lineConfigs.map((config) => (
              <Line
                key={config.dataKey}
                type="monotone"
                dataKey={config.dataKey}
                name={config.name}
                stroke={config.stroke}
                strokeWidth={config.strokeWidth}
                dot={config.dot !== false ? { fill: config.stroke, r: 4 } : false}
                hide={hiddenSeries[config.dataKey]}
                activeDot={{
                  r: 6,
                  onClick: (data) => handleLineClick(data, config.dataKey)
                }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </ChartContainer>
  )
}

export default ComplianceChart;
